<script setup lang="ts">
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

// eslint-disable-next-line
dayjs.extend(duration);

interface Props {
  tournament: TTournament;
}

const props = defineProps<Props>();

const currentTime = ref(dayjs());
const tournamentDetails = ref<TTournamentDetailed>();
const { loadTournamentInfo } = useSmarticoTournaments();
const { isAuthenticated } = storeToRefs(useAuthStore());
const { openLogin } = useAuth();
const remainingTimes = ref<Record<number, string>>({});

const filterTournamentNameTitle = computed(() => {
  return props.tournament.name;
});

const prizeAmount = computed(() => {
  let prize = 'Misto';

  if (props.tournament.prize_pool_short) {
    prize = `${props.tournament.prize_pool_short}`;
  }

  return prize;
});

const topPrizes = computed(() => {
  // Prefer detailed prizes if available
  if (
    tournamentDetails.value?.prizes &&
    tournamentDetails.value.prizes.length
  ) {
    return tournamentDetails.value.prizes.slice(0, 3).map((p) => {
      if (p.points !== undefined && p.points !== null) return `R$${p.points}`;
      if (p.name) return p.name;

      return prizeAmount.value;
    });
  }

  // Fallback: use prizeAmount then placeholders
  return [prizeAmount.value, 'R$30.000', 'R$20.000'];
});

function formatNumber(n: number | null | undefined) {
  if (n === null || n === undefined) return '0';
  try {
    return new Intl.NumberFormat('pt-BR').format(n);
  } catch {
    return String(n);
  }
}

const formattedParticipantCount = computed(() =>
  formatNumber(participantCount.value),
);

const tournamentImageUrl = computed(() => {
  if (import.meta.client && window.innerWidth < 768) {
    return props.tournament.image2_mobile || props.tournament.image2;
  }

  return props.tournament.image2 || props.tournament.image2_mobile;
});

const ribbonText = computed<string | null>(() => {
  const r = String(props.tournament.ribbon || '').trim();

  return r || null;
});

const participantCount = computed(() => {
  // Use registration_count from detailed data or base tournament object.
  return (
    tournamentDetails.value?.registration_count ??
    props.tournament.registration_count ??
    0
  );
});

const userScore = computed(() => {
  const meScore = tournamentDetails.value?.me?.scores;

  if (meScore !== undefined && meScore !== null) return meScore;

  const playerMe = tournamentDetails.value?.players?.find((p) => p.is_me);

  if (playerMe && playerMe.scores !== undefined && playerMe.scores !== null)
    return playerMe.scores;

  return null;
});

const formattedUserScore = computed(() => {
  if (userScore.value === null || userScore.value === undefined) return '---';

  return formatNumber(userScore.value);
});

const tournamentId = computed(
  () => props.tournament.instance_id ?? props.tournament.tournament_id ?? '',
);

async function loadTournamentDetails() {
  if (!props.tournament.instance_id) {
    console.error('Tournament instance ID not available');

    return undefined;
  }

  try {
    if (import.meta.client && window && window._smartico) {
      let details;
      const maxRetries = 10;

      const retryApiCall = async (
        apiCall: () => Promise<any>,
        maxRetries: number,
      ) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const result = await apiCall();

            if (result) {
              return result;
            }
          } catch (err: any) {
            const msg = err?.message || String(err);

            const isSmarticoNullTournament =
              msg.includes("reading 'tournamentId'") ||
              msg.includes('tournamentId');

            // Smartico SDK noise (TournamentItemsTransform null-deref) is
            // recovered via the store's onUpdate callback; don't spam console.
            if (!isSmarticoNullTournament) {
              console.warn(`Retry attempt ${attempt} failed:`, err);
            }

            // If the error indicates an internal Smartico null access (non-retryable), abort early
            if (
              msg.includes('tournamentLobbyInfo') ||
              msg.includes('Cannot read properties of null') ||
              msg.includes('Cannot read properties of undefined')
            ) {
              throw err;
            }

            if (attempt === maxRetries) {
              throw err;
            }

            // Wait before retrying, increasing delay with each attempt
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          }
        }

        throw new Error('API call failed after maximum retries');
      };

      if (isAuthenticated.value && props.tournament.is_user_registered) {
        details = await retryApiCall(
          () =>
            window._smartico.api.getTournamentInstanceInfo(
              props.tournament.instance_id,
            ),
          maxRetries,
        );

        if (details && details.instance_id !== props.tournament.instance_id) {
          details = await retryApiCall(
            () =>
              window._smartico.api.getTournamentInstanceInfo(
                props.tournament.instance_id,
              ),
            maxRetries,
          );
        }

        return details;
      } else {
        details = await retryApiCall(
          () =>
            window._smartico
              .vapi('EN')
              .getTournamentInstanceInfo(props.tournament.instance_id),
          maxRetries,
        );

        return details;
      }
    } else {
      const details = await loadTournamentInfo(props.tournament.instance_id);

      return details;
    }
  } catch (error: any) {
    const msg = String(error?.message || error || '');
    const isSmarticoNullTournament =
      msg.includes("reading 'tournamentId'") || msg.includes('tournamentId');

    if (!isSmarticoNullTournament) {
      console.error(
        `Error loading tournament details ${props.tournament.instance_id}:`,
        error,
      );
    }

    return undefined;
  }
}

async function startTournamentDetailsLoading() {
  const details = await loadTournamentDetails();

  if (details) {
    tournamentDetails.value = details;
  }
  currentTime.value = dayjs();
}

function viewTournament() {
  if (!isAuthenticated.value) {
    openLogin();

    return;
  }

  navigateTo(localeLink('/vip/tournaments/' + tournamentId.value));
}

function formatTimeRemaining(end_time: number): string {
  const now = Date.now();
  let diff = end_time - now;

  if (diff <= 0) return '00:00:00:00';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  diff -= days * 1000 * 60 * 60 * 24;

  const hours = Math.floor(diff / (1000 * 60 * 60));

  diff -= hours * 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));

  diff -= minutes * 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  const pad = (n: number) => n.toString().padStart(2, '0');

  return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

const tryLoadTournamentDetails = (maxRetries = 5, delayMs = 1000) => {
  let retries = 0;

  const attempt = async (): Promise<void> => {
    if (window && window._smartico) {
      await startTournamentDetailsLoading();

      return;
    }

    if (retries < maxRetries) {
      retries++;
      console.log(`Aguardando _smartico, tentativa ${retries}/${maxRetries}`);
      await new Promise((resolve) => setTimeout(resolve, delayMs));

      return attempt();
    }

    console.warn('Falha ao carregar _smartico após tentativas máximas');
  };

  return attempt();
};

let intervalId: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  tryLoadTournamentDetails();

  if (props.tournament?.end_time) {
    intervalId = setInterval(() => {
      if (props.tournament.end_time > Date.now()) {
        remainingTimes.value[props.tournament.instance_id] =
          formatTimeRemaining(props.tournament.end_time);
      }
    }, 1000);
  }
});

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = undefined;
  }
});
</script>
<template>
  <div class="cardWrapper">
    <div class="tournament-card max-w-xl mx-auto select-none w-full box-border">
      <div
        class="bg-[#010612] rounded-[7.4px] p-4 border border-solid border-[#1D2329] cursor-pointer hover:border-[#2D3339] transition-colors"
        @click="viewTournament"
      >
        <!-- Imagem do torneio: div de fundo atrás da imagem (2px maior) em vez de borda -->
        <div
          class="relative rounded-[7.4px] overflow-hidden z-10"
          :class="{
            grayscale: true,
          }"
        >
          <!-- Div de fundo (simula a borda) com 2px a mais em cada lado -->
          <div
            class="absolute -top-[1px] -left-[1px] -right-[1px] -bottom-[1px] rounded-[7.4px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] z-0"
          ></div>

          <div class="relative rounded-[7.4px] z-10">
            <NuxtImg
              :alt="`Tournament-${tournamentId}`"
              class="w-full h-[110px] object-cover rounded-[7.4px] border-[0.65px] border-solid border-[#ff3400]"
              :draggable="false"
              :src="tournamentImageUrl"
            />

            <!-- Ribbon (laranja) -->
            <div
              v-if="ribbonText"
              class="absolute -top-[1px] -left-[1px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] text-white text-[8px] font-semibold px-[7.4px] py-[3.7px] rounded-br-[7.4px] z-20"
            >
              {{ ribbonText }}
            </div>

            <!-- Badge de participantes no canto inferior direito -->
            <div
              class="absolute -bottom-[1px] -right-[1px] bg-gradient-to-r from-[#ff704a] to-[#ff3400] text-white text-[8px] font-semibold px-[7.4px] py-[3.7px] rounded-tl-[7.4px] flex items-center gap-1 shadow-lg z-20"
            >
              <NuxtIcon
                class="text-[10px] text-white"
                name="players"
              />
              <span>{{ formattedParticipantCount }}</span>
            </div>
          </div>
        </div>

        <!-- Conteúdo baixo da imagem -->
        <div class="mt-3 text-center w-full mx-auto">
          <span
            class="text-[15px] text-white font-extrabold leading-tight block w-full truncate overflow-ellipsis"
            >{{ filterTournamentNameTitle }}</span
          >

          <!-- Linha de prêmios (Figma) -->
          <div class="mt-2 flex items-center justify-around gap-1">
            <template
              v-for="(p, idx) in topPrizes"
              :key="idx"
            >
              <div class="flex items-center gap-1 justify-center text-white">
                <NuxtImg
                  alt="Crown Icon"
                  class="h-[16px]"
                  :src="
                    getAssetsUrlForCDN(
                      `/assets/gamification/place-${idx + 1}.png`,
                    )
                  "
                />
                <div
                  class="text-[8px] font-semibold truncate overflow-ellipsis"
                  >{{ p }}</div
                >
              </div>
            </template>
          </div>

          <!-- Sua pontuação -->
          <div class="mt-2 flex justify-center">
            <div
              class="w-full flex items-center justify-between rounded-[7.4px] px-2 py-1"
              style="
                background: linear-gradient(
                  90deg,
                  rgba(255, 255, 255, 0.04) 32.22%,
                  var(--Cyan, rgba(88, 192, 250, 0.23)) 100%
                );
              "
            >
              <div class="text-[8px] text-white/40">SUA PONTUAÇÃO</div>
              <div
                class="text-white font-bold text-[10px]"
                :title="
                  userScore !== null && userScore !== undefined
                    ? userScore.toString()
                    : ''
                "
              >
                {{ formattedUserScore }}
              </div>
            </div>
          </div>

          <!-- Botão full width -->
          <div class="mt-2">
            <button
              class="flex justify-center items-center w-full h-8 bg-bg-primary rounded-[4.4px] px-6 py-3 text-white text-[12px] font-semibold hover:opacity-70 border-solid border border-white/10"
              @click.stop="viewTournament"
            >
              VER TORNEIO
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.ribbon-container {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  z-index: 10;
  min-width: 60px;
  width: auto;
  height: 16px;
  padding: 0 4px;
  background: radial-gradient(
    ellipse 138% 168.3% at 36.76% -16.67%,
    #ff704a 0%,
    #ff3400 100%
  );
  border-radius: 4px;
  outline: 1px solid rgb(252 165 165);
  display: flex;
  align-items: center;
  justify-content: center;
}
/* Ensure consistent sizing: full width in grid, fixed slide width inside PerfilOficialSwiper */
.afiliado-swiper__items .tournament-card {
  box-sizing: border-box;
  flex: 0 0 260px !important;
  min-width: 260px !important;
  width: 260px !important;
  max-width: 260px !important;
}

/* Use wrapper sizing similar to Missions: full in grid, fixed in swiper */
.cardWrapper {
  width: 100%;
  min-width: 260px;
  isolation: isolate;
  contain: layout style paint;
}

/* Fix: ensure borders/padding of children don't increase visible card width */
.afiliado-swiper__items .cardWrapper {
  flex: 0 0 260px !important;
  width: 260px !important;
  min-width: 260px !important;
  max-width: 260px !important;
  box-sizing: border-box !important;
}
</style>
